import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import BaseModal from '../../components/common/BaseModal/BaseModal';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import './AddJobModal.css';
import { jobService } from '../../services/apiService';

const CloneJobModal = ({ isOpen, onClose, onSuccess, setIsLoading, jobData }) => {
    const [jobName, setJobName] = useState('');
    const [error, setError] = useState('');

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setJobName('');
            setError('');
        }
    }, [isOpen]);

    const handleChange = (value) => {
        setJobName(value);
        if (error) setError('');
    };

    const validateForm = () => {
        if (!jobName.trim()) {
            setError('Job Name is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const normalizeFilter = (val) => {
                if (!val) return [];
                if (Array.isArray(val)) return val.filter(Boolean);
                return val.split(',').filter(Boolean);
            };
            // const selectedObjectTypes = jobData.objectTypeOptions
            //     .filter(opt => opt.key !== 'selectAll' && objectTypes[opt.key])
            //     .map(opt => opt.key === 'fileFormat' ? 'FILE_FORMAT' : opt.key.toUpperCase());

            const notifEnabled = !!jobData.is_notification_enabled;

            const payload = {
                name: jobName,
                source_connection_id: jobData.source_connection_id,
                target_connection_id: jobData.target_connection_id,
                automated_comparison: jobData.automated_comparison,
                global_include_prefix: normalizeFilter(jobData.global_include_prefix),
                global_include_suffix: normalizeFilter(jobData.global_include_suffix),
                global_exclude_prefix: normalizeFilter(jobData.global_exclude_prefix),
                global_exclude_suffix: normalizeFilter(jobData.global_exclude_suffix),
                schema_pairs: jobData.schema_pairs.map(pair => ({
                    source_schema: pair.source_schema,
                    target_schema: pair.target_schema,
                    include_prefix: normalizeFilter(pair.include_prefix),
                    include_suffix: normalizeFilter(pair.include_suffix),
                    exclude_prefix: normalizeFilter(pair.exclude_prefix),
                    exclude_suffix: normalizeFilter(pair.exclude_suffix)
                })),
                object_types: jobData.object_types.split(',').map(t => t.trim()),
                github_connection_id: jobData?.github_connection?.id ?? null,
                is_github_push_enabled: !!jobData.is_github_push_enabled,
                schedule: jobData.schedule?.is_enabled ? {
                    is_enabled: true,
                    schedule_type: jobData.schedule.schedule_type,
                    run_at: jobData.schedule.run_at,
                    cron_expression: jobData.schedule.cron_expression,
                    retry_attempts: jobData.schedule.retry_attempts ?? null,
                    retry_offset_seconds: jobData.schedule.retry_offset_seconds ?? null,
                } : {
                    is_enabled: false,
                    schedule_type: 'ONE_TIME',
                    run_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
                    cron_expression: '',
                    retry_attempts: null,
                    retry_offset_seconds: null,
                },
                is_notification_enabled: notifEnabled,
                notification_emails_failure: notifEnabled ? (jobData.notification_emails_failure ?? []) : [],
                notification_emails_success: notifEnabled ? (jobData.notification_emails_success ?? []) : [],
                notification_emails_success_with_changes: notifEnabled ? (jobData.notification_emails_success_with_changes ?? []) : [],
            };

            const response = await jobService.saveJob(payload);
            if (response) {
                toast.success('Job saved successfully');
                await onSuccess?.();
                handleModalClose();
            }
        } catch (error) {
            toast.error(error.message || 'Failed to clone job. Please try again.');
            console.error('Clone job error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalClose = () => {
        setJobName('');
        setError('');
        onClose();
    };

    const footer = (
        <>
            <button
                type="button"
                className="btn btn-cancel"
                onClick={handleModalClose}
            >
                Cancel
            </button>
            <button
                type="submit"
                className="btn btn-primary"
                onClick={handleSubmit}
            >
                Save
            </button>
        </>
    );

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleModalClose}
            title="Add New Job"
            footer={footer}
        >
            <form onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group form-group-full">
                        <label>
                            Job Name <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="e.g., Production vs UAT Comparison"
                            value={jobName}
                            onChange={(e) => handleChange(e.target.value)}
                            className={error ? 'error' : ''}
                            autoFocus
                        />
                        <ErrorMessage message={error} />
                    </div>
                </div>
            </form>
        </BaseModal>
    );
};

export default CloneJobModal;